export const SITE_URL = "https://www.aamschool.in";
export const OG_IMAGE_URL = `${SITE_URL}/aamschool-share.png`;

const JOB_FIELD_ALIASES = {
  postName: ["postName", "post_name", "title"],
  totalVacancy: ["totalVacancy", "total_vacancy", "vacancies", "posts"],
  eligibility: ["eligibilityCriteria", "eligibility", "ageLimit", "age_limit"],
  importantDates: ["importantDates", "important_dates", "important_dates_table"],
  officialLinks: ["officialLinks", "important_links", "importantLinks", "official_links"],
  payScale: ["payScale", "pay_scale", "salary", "salaryScale", "salary_scale"],
  location: [
    "jobLocation",
    "job_location",
    "location",
    "locations",
    "postingLocation",
    "posting_location",
    "placeOfPosting",
    "place_of_posting",
    "examCity",
    "exam_city",
  ],
  shortDescription: ["shortDescription", "short_description", "short_description_text", "description"],
  conductingBody: ["conductingBody", "conducting_body", "organization", "department", "board"],
};

const MONTH_LOOKUP = {
  jan: 0,
  january: 0,
  feb: 1,
  february: 1,
  mar: 2,
  march: 2,
  apr: 3,
  april: 3,
  may: 4,
  jun: 5,
  june: 5,
  jul: 6,
  july: 6,
  aug: 7,
  august: 7,
  sep: 8,
  sept: 8,
  september: 8,
  oct: 9,
  october: 9,
  nov: 10,
  november: 10,
  dec: 11,
  december: 11,
};

const INDIA_REGIONS = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

const normalizeKey = (value) =>
  String(value || "")
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase();

const slugify = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-+/g, "-");

const isObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const cleanText = (value) => String(value || "").replace(/\s+/g, " ").trim();

export const prettifyJobDateLabel = (value) =>
  cleanText(value)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const findFirstValue = (target, aliases = []) => {
  const directMatch = aliases.find((alias) => target?.[alias] != null && target?.[alias] !== "");
  if (directMatch) return target[directMatch];

  const normalizedAliasSet = new Set(aliases.map(normalizeKey));
  const entry = Object.entries(target || {}).find(([key, value]) => {
    if (value == null || value === "") return false;
    return normalizedAliasSet.has(normalizeKey(key));
  });

  return entry ? entry[1] : "";
};

const flattenTextValues = (value) => {
  if (value == null || value === "") return [];
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return [String(value)];
  }
  if (Array.isArray(value)) {
    return value.flatMap((item) => flattenTextValues(item));
  }
  if (isObject(value)) {
    return Object.entries(value).flatMap(([key, child]) =>
      flattenTextValues(child).map((text) => `${key}: ${text}`)
    );
  }
  return [];
};

const summarizeValue = (value, fallback = "Not announced yet") => {
  const flattened = flattenTextValues(value).map(cleanText).filter(Boolean);
  return flattened[0] || fallback;
};

const findValueByPatterns = (target, patterns = []) => {
  const normalizedPatterns = patterns.map((item) => normalizeKey(item));
  const entries = Object.entries(target || {});
  const match = entries.find(([key, value]) => {
    if (value == null || value === "") return false;
    const normalizedKey = normalizeKey(key);
    return normalizedPatterns.some((pattern) => normalizedKey.includes(pattern));
  });

  return match ? match[1] : "";
};

const getJobImportantDates = (job) => {
  const raw = findFirstValue(job, JOB_FIELD_ALIASES.importantDates);
  return isObject(raw) ? raw : {};
};

export const normalizeJobImportantDates = (importantDates) => {
  if (!isObject(importantDates)) return [];

  return Object.entries(importantDates)
    .map(([key, value]) => [cleanText(key), cleanText(value)])
    .filter(([key, value]) => {
      if (!key || !value) return false;
      if (normalizeKey(key) === "na" && normalizeKey(value) === "na") return false;
      return true;
    })
    .map(([key, value]) => ({
      key,
      label: prettifyJobDateLabel(key),
      value,
    }));
};

const getJobOfficialLinks = (job) => {
  const raw = findFirstValue(job, JOB_FIELD_ALIASES.officialLinks);
  return isObject(raw) ? raw : {};
};

const readJobPayScale = (job) => cleanText(findFirstValue(job, JOB_FIELD_ALIASES.payScale));

const readJobShortDescription = (job) => cleanText(findFirstValue(job, JOB_FIELD_ALIASES.shortDescription));

const readJobConductingBody = (job) =>
  cleanText(findFirstValue(job, JOB_FIELD_ALIASES.conductingBody)) || "AAMSchool";

const readJobPostName = (job) =>
  cleanText(findFirstValue(job, JOB_FIELD_ALIASES.postName)) || "Job Update";

const readJobVacancy = (job) => {
  const directValue = cleanText(findFirstValue(job, JOB_FIELD_ALIASES.totalVacancy));
  if (directValue) return directValue.replace(/\b(posts?|vacancies)\b/gi, "").trim();

  const vacancySource =
    job?.vacancyDetails ||
    job?.vacancy_details ||
    job?.vacancy_detail ||
    "";
  const matched = summarizeValue(vacancySource, "").match(/\b\d[\d,]*\b/);
  return matched ? matched[0] : "";
};

const extractYearFromText = (value) => {
  const match = String(value || "").match(/\b(20\d{2})\b/);
  return match ? match[1] : "";
};

const readJobYear = (job) => {
  const importantDates = getJobImportantDates(job);
  const dateEntry = Object.values(importantDates).find((value) => extractYearFromText(value));
  return (
    extractYearFromText(job?.title) ||
    extractYearFromText(readJobPostName(job)) ||
    extractYearFromText(dateEntry) ||
    String(new Date().getFullYear())
  );
};

export const extractJobLastDate = (job) => {
  const importantDates = getJobImportantDates(job);
  return summarizeValue(
    findValueByPatterns(importantDates, ["last date", "apply last date", "closing date"]),
    ""
  );
};

const readJobExamDate = (job) => {
  const importantDates = getJobImportantDates(job);
  return summarizeValue(findValueByPatterns(importantDates, ["exam date", "exam city"]), "");
};

const readJobAnswerKey = (job) => {
  const importantDates = getJobImportantDates(job);
  const officialLinks = getJobOfficialLinks(job);
  return summarizeValue(
    findValueByPatterns(importantDates, ["answer key"]) ||
      findValueByPatterns(officialLinks, ["answer key"]),
    ""
  );
};

const readJobAdmitCard = (job) => {
  const importantDates = getJobImportantDates(job);
  const officialLinks = getJobOfficialLinks(job);
  return summarizeValue(
    findValueByPatterns(importantDates, ["admit card", "hall ticket"]) ||
      findValueByPatterns(officialLinks, ["admit card", "hall ticket"]),
    ""
  );
};

const readJobResult = (job) => {
  const importantDates = getJobImportantDates(job);
  const officialLinks = getJobOfficialLinks(job);
  return summarizeValue(
    findValueByPatterns(importantDates, ["result"]) ||
      findValueByPatterns(officialLinks, ["result"]),
    ""
  );
};

export const buildJobListTitle = (job, label = "") => {
  const title = cleanText(job?.title || readJobPostName(job));
  const normalizedLabel = cleanText(label).toLowerCase();

  if (!title) {
    return {
      title: "",
      dateLabel: "",
      fullTitle: "",
    };
  }

  let dateLabel = "";

  if (normalizedLabel === "latest-jobs" || normalizedLabel === "admission") {
    const lastDate = extractJobLastDate(job);
    dateLabel = lastDate ? `Last Date: ${lastDate}` : "";
  } else if (normalizedLabel === "admit-cards") {
    const examDate = readJobExamDate(job);
    dateLabel = examDate ? `Exam Date: ${examDate}` : "";
  } else if (normalizedLabel === "answer-keys") {
    const answerKeyDate = readJobAnswerKey(job);
    dateLabel = answerKeyDate ? `Released: ${answerKeyDate}` : "";
  } else if (normalizedLabel === "latest-results") {
    const resultDate = readJobResult(job);
    dateLabel = resultDate ? `Result Declared: ${resultDate}` : "";
  }

  return {
    title,
    dateLabel,
    fullTitle: dateLabel ? `${title} - ${dateLabel}` : title,
  };
};

export const detectJobStatus = (job) => {
  if (readJobAnswerKey(job)) return "answer-key";
  if (readJobAdmitCard(job)) return "admit-card";
  if (readJobResult(job)) return "result";
  if (readJobExamDate(job)) return "exam-date";
  return "apply-online";
};

export const getJobStatusLabel = (job) => {
  const status = detectJobStatus(job);
  if (status === "answer-key") return "Answer Key OUT";
  if (status === "admit-card") return "Admit Card Released";
  if (status === "result") return "Result OUT";
  if (status === "exam-date") return "Exam Date Announced";
  return "Apply Online";
};

export const buildJobSeoSlug = (job) => {
  const postName = slugify(readJobPostName(job));
  const year = slugify(readJobYear(job));
  const vacancy = slugify(readJobVacancy(job));
  const slugParts = [postName, year, "apply-online"];

  if (vacancy) {
    slugParts.push(vacancy, "posts");
  }

  return slugParts.filter(Boolean).join("-");
};

export const buildJobSeoTitle = (job) => {
  const postName = readJobPostName(job);
  const year = readJobYear(job);
  const totalVacancy = readJobVacancy(job);
  const lastDate = extractJobLastDate(job);
  const examDate = readJobExamDate(job);
  const status = detectJobStatus(job);

  if (status === "answer-key") {
    return `${postName} Answer Key ${year} OUT - Download Now`;
  }

  if (status === "admit-card") {
    return `${postName} Admit Card ${year} Released - Download Now`;
  }

  if (status === "result") {
    return `${postName} Result ${year} OUT - Check Now`;
  }

  if (status === "exam-date" && examDate) {
    return `${postName} Exam Date ${year} - ${examDate}, Admit Card Soon`;
  }

  const vacancyText = totalVacancy ? `${totalVacancy} Posts, ` : "";
  const lastDateText = lastDate ? `Last Date ${lastDate}, ` : "";
  return `${postName} ${year} - ${vacancyText}${lastDateText}Apply Online`;
};

export const buildJobMetaDescription = (job) => {
  const shortDescription = readJobShortDescription(job);
  if (shortDescription) return shortDescription;

  const postName = readJobPostName(job);
  const year = readJobYear(job);
  const totalVacancy = readJobVacancy(job);
  const lastDate = extractJobLastDate(job);
  const vacancyText = totalVacancy ? ` for ${totalVacancy} posts` : "";
  const lastDateText = lastDate ? ` Last date ${lastDate}.` : "";

  return `Apply for ${postName} ${year}${vacancyText}. Check eligibility, age limit, exam date, and direct apply link.${lastDateText}`.trim();
};

const padDatePart = (value) => String(value).padStart(2, "0");

const isValidDateParts = (year, monthIndex, day) => {
  const date = new Date(Date.UTC(year, monthIndex, day));
  return (
    Number.isInteger(year) &&
    Number.isInteger(monthIndex) &&
    Number.isInteger(day) &&
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === monthIndex &&
    date.getUTCDate() === day
  );
};

export const parseDateToIso = (value) => {
  if (value == null || value === "") return "";

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${value.getUTCFullYear()}-${padDatePart(value.getUTCMonth() + 1)}-${padDatePart(value.getUTCDate())}`;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : parseDateToIso(date);
  }

  const text = cleanText(value);
  if (!text) return "";

  const directIsoMatch = text.match(/\b(20\d{2})-(\d{2})-(\d{2})\b/);
  if (directIsoMatch) return directIsoMatch[0];

  const slashMatch = text.match(/\b(\d{1,2})[-/.](\d{1,2})[-/.](20\d{2})\b/);
  if (slashMatch) {
    const day = Number(slashMatch[1]);
    const monthIndex = Number(slashMatch[2]) - 1;
    const year = Number(slashMatch[3]);
    if (isValidDateParts(year, monthIndex, day)) {
      return `${year}-${padDatePart(monthIndex + 1)}-${padDatePart(day)}`;
    }
  }

  const namedMonthMatch = text.match(/\b(\d{1,2})\s+([A-Za-z]+)\s*,?\s*(20\d{2})\b/);
  if (namedMonthMatch) {
    const day = Number(namedMonthMatch[1]);
    const monthIndex = MONTH_LOOKUP[namedMonthMatch[2].toLowerCase()];
    const year = Number(namedMonthMatch[3]);
    if (isValidDateParts(year, monthIndex, day)) {
      return `${year}-${padDatePart(monthIndex + 1)}-${padDatePart(day)}`;
    }
  }

  const reverseNamedMonthMatch = text.match(/\b([A-Za-z]+)\s+(\d{1,2}),?\s*(20\d{2})\b/);
  if (reverseNamedMonthMatch) {
    const monthIndex = MONTH_LOOKUP[reverseNamedMonthMatch[1].toLowerCase()];
    const day = Number(reverseNamedMonthMatch[2]);
    const year = Number(reverseNamedMonthMatch[3]);
    if (isValidDateParts(year, monthIndex, day)) {
      return `${year}-${padDatePart(monthIndex + 1)}-${padDatePart(day)}`;
    }
  }

  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? "" : parseDateToIso(parsed);
};

export const formatIsoDateForUi = (value) => {
  const isoValue = parseDateToIso(value);
  if (!isoValue) return "";

  const date = new Date(`${isoValue}T00:00:00Z`);
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
};

export const resolveJobDatePosted = (job) =>
  parseDateToIso(
    job?.date_posted ||
      job?.datePosted ||
      job?.postDate ||
      job?.post_date ||
      job?.createdAt ||
      job?.date_updated ||
      job?.dateUpdated ||
      job?.updatedAt ||
      ""
  );

const detectIndianRegion = (text) => {
  const normalizedText = normalizeKey(text);
  return INDIA_REGIONS.find((region) => normalizedText.includes(normalizeKey(region))) || "";
};

const cleanLocationText = (value) =>
  cleanText(value)
    .replace(/^(job\s*location|posting\s*location|location|place\s*of\s*posting)\s*:\s*/i, "")
    .trim();

const LOCATION_OBJECT_KEYS = [
  "location_text",
  "locationText",
  "city",
  "district",
  "state",
  "states",
  "region",
  "addressRegion",
  "country",
  "addressCountry",
  "name",
  "label",
  "value",
];

const formatLocationValue = (value) => {
  if (value == null || value === "") return [];

  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return [cleanText(value)];
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => formatLocationValue(item));
  }

  if (!isObject(value)) {
    return [];
  }

  const preferredValues = LOCATION_OBJECT_KEYS.map((key) => value?.[key])
    .flatMap((item) => formatLocationValue(item))
    .filter(Boolean);

  if (preferredValues.length) {
    return [[...new Set(preferredValues)].join(", ")];
  }

  return flattenTextValues(value)
    .map((item) => cleanLocationText(item.replace(/^[^:]+:\s*/, "")))
    .filter(Boolean);
};

const extractLocationHint = (value) => {
  const text = summarizeValue(value, "");
  if (!text) return "";

  if (/^(job\s*location|posting\s*location|location|place\s*of\s*posting)\s*:/i.test(text)) {
    return cleanLocationText(text);
  }

  return "";
};

const collectLocationTexts = (job) => {
  const directLocations = formatLocationValue(findFirstValue(job, JOB_FIELD_ALIASES.location));
  const inferredLocation = extractLocationHint(
    job?.vacancyDetails || job?.vacancy_details || job?.vacancy_detail || ""
  );

  return [...directLocations, inferredLocation].filter(Boolean);
};

export const resolveJobLocationText = (job) => {
  const locationTexts = collectLocationTexts(job).map(cleanLocationText).filter(Boolean);
  const explicitLocation = locationTexts[0];
  if (explicitLocation) return explicitLocation;

  return "India";
};

export const buildJobLocationSchema = (job) => {
  const locationText = resolveJobLocationText(job);
  const region = detectIndianRegion(locationText);

  return {
    "@type": "Place",
    address: {
      "@type": "PostalAddress",
      addressCountry: /india|\bIN\b/i.test(locationText) ? "IN" : locationText || "IN",
      ...(region ? { addressRegion: region } : {}),
    },
  };
};

export const inferJobEmploymentType = (job) => {
  const searchSpace = [
    readJobPostName(job),
    cleanText(job?.title),
    readJobShortDescription(job),
    summarizeValue(job?.description, ""),
    summarizeValue(job?.vacancyDetails || job?.vacancy_details || job?.vacancy_detail, ""),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (/\b(intern(ship)?|apprentice)\b/.test(searchSpace)) return "INTERN";
  if (/\b(contract|contractual|outsource|consultant)\b/.test(searchSpace)) return "CONTRACTOR";
  if (/\btemporary|temp\b/.test(searchSpace)) return "TEMPORARY";
  if (/\bpart[- ]?time\b/.test(searchSpace)) return "PART_TIME";
  return "FULL_TIME";
};

const normalizeNumberString = (value) => String(value || "").replace(/,/g, "").trim();

export const parseJobBaseSalary = (job) => {
  const payScale = readJobPayScale(job);
  if (!payScale) return null;

  const text = payScale.toLowerCase();
  const amountMatches = [...payScale.matchAll(/\b\d[\d,]*\b/g)].map((match) =>
    Number(normalizeNumberString(match[0]))
  );

  if (!amountMatches.length || amountMatches.some((amount) => !Number.isFinite(amount))) {
    return null;
  }

  const [minValue] = amountMatches;
  const maxValue = amountMatches.length > 1 ? Math.max(...amountMatches) : undefined;
  const unitText = /\b(year|annual|annum|p\.a)\b/.test(text)
    ? "YEAR"
    : /\b(day|daily|per day)\b/.test(text)
      ? "DAY"
      : "MONTH";

  return {
    "@type": "MonetaryAmount",
    currency: "INR",
    value: {
      "@type": "QuantitativeValue",
      minValue,
      ...(maxValue && maxValue !== minValue ? { maxValue } : {}),
      unitText,
    },
  };
};

export const resolveDirectApplyUrl = (job) => {
  const officialLinks = getJobOfficialLinks(job);
  const directApplyLink =
    findValueByPatterns(officialLinks, ["apply online", "apply link", "registration"]) ||
    officialLinks?.apply_online ||
    officialLinks?.applyLink ||
    "";

  return cleanText(directApplyLink || job?.source || job?.source_link || "");
};

export const buildJobSeoMeta = (job) => {
  const datePostedIso = resolveJobDatePosted(job);
  const locationText = resolveJobLocationText(job);
  const employmentType = inferJobEmploymentType(job);
  const baseSalary = parseJobBaseSalary(job);

  return {
    datePostedIso,
    datePostedLabel: formatIsoDateForUi(datePostedIso),
    locationText,
    employmentType,
    salaryText: readJobPayScale(job),
    baseSalary,
    directApplyUrl: resolveDirectApplyUrl(job),
  };
};

export const buildJobFaqEntries = (job) => {
  const postName = readJobPostName(job);
  const lastDate = extractJobLastDate(job);
  const vacancy = readJobVacancy(job);
  const eligibility = summarizeValue(findFirstValue(job, JOB_FIELD_ALIASES.eligibility));

  return [
    {
      question: `What is the last date for ${postName}?`,
      answer: lastDate
        ? `The last date for ${postName} is ${lastDate}. Candidates should complete the process before the deadline.`
        : `The last date for ${postName} has not been clearly published in the available data, so candidates should verify the official notice.`,
    },
    {
      question: `How many vacancies are available for ${postName}?`,
      answer: vacancy
        ? `${postName} has ${vacancy} advertised posts in the available vacancy details.`
        : `The total number of vacancies for ${postName} is not clearly available in the current data, so candidates should verify the official notification.`,
    },
    {
      question: `What is the eligibility for ${postName}?`,
      answer: eligibility && eligibility !== "Not announced yet"
        ? `Eligibility details for ${postName} include: ${eligibility}. Candidates should confirm the full criteria from the official notice.`
        : `Candidates should check the official notification for ${postName} to confirm eligibility, age limit, and required qualifications.`,
    },
  ];
};

export const buildJobBadgeItems = (job) => {
  const lastDate = extractJobLastDate(job);
  const admitCard = readJobAdmitCard(job);
  const answerKey = readJobAnswerKey(job);
  const result = readJobResult(job);

  return [
    lastDate
      ? { tone: "danger", icon: "🔴", label: "Last Date", value: lastDate }
      : null,
    admitCard
      ? { tone: "success", icon: "🟢", label: "Admit Card", value: admitCard }
      : null,
    answerKey
      ? { tone: "info", icon: "🔵", label: "Answer Key", value: answerKey }
      : null,
    result
      ? { tone: "warning", icon: "🟡", label: "Result", value: result }
      : null,
  ].filter(Boolean);
};

export const buildJobStructuredData = (job, canonicalUrl) => {
  const postName = readJobPostName(job);
  const description = buildJobMetaDescription(job);
  const faqEntries = buildJobFaqEntries(job);
  const lastDate = extractJobLastDate(job);
  const validThroughIso = parseDateToIso(lastDate);
  const seoMeta = buildJobSeoMeta(job);
  const totalVacancy = readJobVacancy(job);
  const advertisementNo = cleanText(job?.advertisementNo || job?.advertisement_no || "");
  const conductingBody = readJobConductingBody(job);
  const breadcrumbName = getJobStatusLabel(job);

  return {
    faqSchema: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqEntries.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    },
    jobPostingSchema: {
      "@context": "https://schema.org",
      "@type": "JobPosting",
      title: postName,
      description,
      url: canonicalUrl,
      datePosted: seoMeta.datePostedIso || undefined,
      validThrough: validThroughIso || undefined,
      employmentType: seoMeta.employmentType || undefined,
      jobLocation: buildJobLocationSchema(job),
      baseSalary: seoMeta.baseSalary || undefined,
      identifier: advertisementNo
        ? {
            "@type": "PropertyValue",
            name: conductingBody,
            value: advertisementNo,
          }
        : undefined,
      hiringOrganization: {
        "@type": "Organization",
        name: conductingBody,
      },
      directApply: Boolean(seoMeta.directApplyUrl) || undefined,
      totalJobOpenings: totalVacancy || undefined,
    },
    breadcrumbSchema: {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: `${SITE_URL}/`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Jobs",
          item: `${SITE_URL}/latest-jobs`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: breadcrumbName,
          item: canonicalUrl,
        },
      ],
    },
  };
};

export const HOME_CATEGORY_SUMMARIES = [
  {
    href: "/latest-jobs",
    title: "Latest Government Jobs",
    summary:
      "Track current recruitment updates, application windows, and official notification links in one place.",
  },
  {
    href: "/admit-card",
    title: "Admit Cards",
    summary:
      "Check hall ticket releases, exam dates, and important reporting instructions for major exams.",
  },
  {
    href: "/results",
    title: "Results",
    summary:
      "Review result announcements, score updates, merit lists, and next-step notices published by recruiting bodies.",
  },
  {
    href: "/admission",
    title: "Admissions",
    summary:
      "Find admission forms, entrance-related notices, and counseling updates for education-focused opportunities.",
  },
  {
    href: "/answer-key",
    title: "Answer Keys",
    summary:
      "Follow provisional and final answer key releases along with objection windows where available.",
  },
  {
    href: "/syllabus",
    title: "Syllabus",
    summary:
      "Use organized syllabus pages to understand exam topics and plan preparation more effectively.",
  },
];

export const formatDisplayDate = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
};

export const extractPrimaryDate = (importantDates) => {
  const normalizedDates = normalizeJobImportantDates(importantDates);
  if (!normalizedDates.length) return "";

  const priorityKeys = [
    "online apply last date",
    "online apply start date",
    "exam date",
    "result date",
    "admit card",
    "notification date",
  ];

  const normalizedEntries = normalizedDates.map(({ key, value }) => [
    String(key || "").trim().toLowerCase(),
    value,
  ]);

  for (const priorityKey of priorityKeys) {
    const match = normalizedEntries.find(([key]) => key === priorityKey);
    if (match?.[1]) {
      return `${priorityKey.replace(/\b\w/g, (char) => char.toUpperCase())}: ${match[1]}`;
    }
  }

  const firstEntry = normalizedDates[0];
  return firstEntry ? `${firstEntry.label}: ${firstEntry.value}` : "";
};

export const buildJobListSummary = (job) => {
  const title = String(job?.title || "").trim();
  const dateSummary = extractJobLastDate(job)
    ? `Last Date: ${extractJobLastDate(job)}`
    : extractPrimaryDate(job?.importantDates);

  if (dateSummary) {
    return `${title} update summary with key recruitment timing. ${dateSummary}.`;
  }

  return `${title} update with important recruitment information, key dates, and official links.`;
};

export const buildCanonicalUrl = (path = "/") => {
  if (!path || path === "/") return `${SITE_URL}/`;
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
};

export const getJobSlug = (job) => job?.slugWithId || job?.slugurl || buildJobSeoSlug(job) || "";
